import {
  FLYER_MAX_BYTES,
  FLYER_TARGET_BYTES,
  flyerSelectionError,
  normalizeFlyerMime,
  type FlyerMime,
} from './event-flyer';

const FLYER_MAX_EDGE = 2400;
const FLYER_MIN_EDGE = 960;
const CARD_MAX_EDGE = 1600;
const CARD_MIN_EDGE = 675;
const CARD_TARGET_BYTES = 400 * 1024;
const QUALITIES = [0.9, 0.82, 0.74, 0.66];

function extensionFor(type: FlyerMime): string {
  if (type === 'image/png') return 'png';
  if (type === 'image/webp') return 'webp';
  return 'jpg';
}

function renamed(name: string, type: FlyerMime): string {
  const base = name.replace(/\.[^.]+$/, '') || 'flyer';
  return `${base}.${extensionFor(type)}`;
}

function canvasBlob(
  source: ImageBitmap,
  width: number,
  height: number,
  type: FlyerMime,
  quality: number,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  if (type === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(source, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));
}

async function compressFlyer(
  file: File,
  opts: { maxEdge: number; minEdge: number; targetBytes: number; tooLarge: string },
): Promise<File> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    let scale = longest > opts.maxEdge ? opts.maxEdge / longest : 1;
    let best: { blob: Blob; type: FlyerMime } | null = null;

    while (scale > 0.15) {
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      for (const quality of QUALITIES) {
        let type: FlyerMime = 'image/webp';
        let blob = await canvasBlob(bitmap, width, height, type, quality);
        if (!blob) {
          type = 'image/jpeg';
          blob = await canvasBlob(bitmap, width, height, type, quality);
        }
        if (!blob) continue;
        if (!best || blob.size < best.blob.size) best = { blob, type };
        if (blob.size <= opts.targetBytes) {
          return new File([blob], renamed(file.name, type), { type });
        }
      }
      if (Math.max(width, height) <= opts.minEdge) break;
      scale *= 0.8;
    }

    if (best && best.blob.size <= FLYER_MAX_BYTES) {
      return new File([best.blob], renamed(file.name, best.type), { type: best.type });
    }
    throw new Error(opts.tooLarge);
  } finally {
    bitmap.close();
  }
}

/**
 * Keep files that are already about 1 MB. Larger JPEG/PNG/WebP images are
 * scaled and re-encoded so the flyer stays readable and under the server limit.
 */
async function prepareImageFile(
  file: File,
  opts: { targetBytes: number; maxEdge: number; minEdge: number; tooLarge: string },
): Promise<File> {
  const message = flyerSelectionError(file);
  if (message) throw new Error(message);
  const declared = file.type.trim() ? normalizeFlyerMime(file.type) : null;
  if (declared && file.size <= opts.targetBytes) return file;
  if (!declared && file.size <= opts.targetBytes) return file;
  return compressFlyer(file, opts);
}

export async function prepareFlyerFile(file: File): Promise<File> {
  return prepareImageFile(file, {
    targetBytes: FLYER_TARGET_BYTES,
    maxEdge: FLYER_MAX_EDGE,
    minEdge: FLYER_MIN_EDGE,
    tooLarge: 'This image is still too large after compression. Try a smaller flyer.',
  });
}

/** Gallery and page photos: longest edge about 2000px, high quality, still under 1.5 MB. */
export async function prepareContentPhotoFile(file: File): Promise<File> {
  const message = flyerSelectionError(file);
  if (message) throw new Error(message);
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const longest = Math.max(bitmap.width, bitmap.height);
  bitmap.close();
  if (longest <= 2000 && file.size <= 1200 * 1024) return file;
  return compressFlyer(file, {
    maxEdge: 2000,
    minEdge: 1200,
    targetBytes: 1200 * 1024,
    tooLarge: 'This image is still too large after compression. Try a smaller photo.',
  });
}

/** Landscape card art: longest edge about 1600px and about 400 KB. */
export async function prepareCardImageFile(file: File): Promise<File> {
  return prepareImageFile(file, {
    targetBytes: CARD_TARGET_BYTES,
    maxEdge: CARD_MAX_EDGE,
    minEdge: CARD_MIN_EDGE,
    tooLarge: 'This image is still too large after compression. Try a smaller card image.',
  });
}
